/* eslint-disable jsx-a11y/anchor-is-valid */
import _ from 'underscore';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ReactTemplate } from '@bingads-webui-react/react-template';
import { DefaultButton, Dropdown, Link } from '@fluentui/react';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { CodeSnippet, SeeExample } from '@bingads-webui-campaign-react/conversion-wizard-components';

import { useEventTag } from '../utils/use-event-tag';
import { getCopyEventTagViewStyles } from '../styles/copy-event-tag-view-styles';

// Allows the user to view and copy the event tag for their conversion goal
function CopyEventTagViewUnstyled(props) {
  const {
    action,
    classes,
    currencyCode,
    goalValue,
    goalValueType,
    onCopyToClipboardComplete,
    onViewEmailInstructionsClick,
  } = props;

  const i18n = useContext(I18nContext);
  const [eventTrackingMethod, setEventTrackingMethod] = useState();

  const { functionName, getEventTag, getExampleCodeSnippet } = useEventTag({
    action,
    currencyCode,
    goalValue,
    goalValueType,
  });

  const eventTrackingOptions = useMemo(() => [
    { key: 'InlineAction', text: i18n.getString(_TL_('Track event on inline action.')) },
    { key: 'PageLoad', text: i18n.getString(_TL_('Track event on page load.')) },
  ], [i18n]);

  const handleChangeEventTrackingMethod = useCallback((_ev, selected) => setEventTrackingMethod(selected.key), []);

  const eventTag = useMemo(() => getEventTag(eventTrackingMethod === 'PageLoad'), [eventTrackingMethod, getEventTag]);
  const exampleCodeSnippet = useMemo(() => getExampleCodeSnippet(eventTrackingMethod === 'PageLoad'), [eventTrackingMethod, getExampleCodeSnippet]);

  const handleCopyClick = useCallback(() => {
    navigator.clipboard.writeText(eventTag)
      .then(() => {
        onCopyToClipboardComplete({
          error: null,
          message: i18n.getString(_TL_('Event tag copied!')),
          tag: eventTag,
        });
      })
      .catch((err) => {
        onCopyToClipboardComplete({
          error: typeof err === 'object' ? JSON.stringify(err) : err,
          message: i18n.getString(_TL_('Event tag copy failed!')),
          tag: eventTag,
        });
      });
  }, [eventTag, onCopyToClipboardComplete, i18n]);

  return (
    <div className={classNames('copy-event-tag-view', classes.copyEventTagView)}>
      <p>
        <ReactTemplate
          model={{ b: text => <strong>{text}</strong> }}
          template={i18n.getString(_TL_('To achieve your conversion goal setting, you will need to set up a {{b}}Custom event tag{{/b}} for tracking specific interactions.'))}
        />
      </p>

      <h2>{i18n.getString(_TL_('Event Tag'))}</h2>

      <p><strong>{i18n.getString(_TL_('When do you want to track this event?'))}</strong></p>
      <p>{i18n.getString(_TL_('We will give you instructions for setting up the event tag based on what you select.'))}</p>
      <Dropdown
        className="event-tracking-dropdown"
        ariaLabel={i18n.getString(_TL_('Event tag option'))}
        selectedKey={eventTrackingMethod}
        onChange={handleChangeEventTrackingMethod}
        options={eventTrackingOptions}
        placeholder={i18n.getString(_TL_('Please select an option'))}
      />

      {eventTrackingMethod && (
        <React.Fragment>
          <p><strong>{i18n.getString(_TL_('Instructions:'))}</strong></p>

          <p>
            {i18n.getString(eventTrackingMethod === 'PageLoad' ?
              _TL_('On each page where this event happens (for example, your order confirmation page or purchase completion page), copy this tag and paste it in between the <head> and </head> tags right after the base tag.') :
              _TL_('Copy the snippet below and paste it in between the <head></head> tags of the page(s) you\'d like to track, right after the base UET tag. Then, call {{functionName}} when someone clicks on the chosen link or button.'), { functionName })}
          </p>

          <p>
            <ReactTemplate
              model={{ a: text => <Link className="view-email-instructions" onClick={onViewEmailInstructionsClick}>{text}</Link> }}
              template={i18n.getString(_TL_('You can also {{a}}send the instructions to a developer{{/a}} if not sure how to do it manually.'))}
            />
          </p>

          <CodeSnippet
            aria-label={i18n.getString(_TL_('Event tag code'))}
            className="event-tag-code"
            codeTemplate={eventTag}
          />

          <p>
            <DefaultButton className="copy-event-tag" onClick={handleCopyClick}>
              {i18n.getString(_TL_('Copy event tag'))}
            </DefaultButton>
          </p>

          <SeeExample
            aria-label={i18n.getString(_TL_('Event tag example'))}
            className="event-tag-example"
            i18n={i18n}
            message={i18n.getString(_TL_('The UET tag should be pasted on every page of your website. The event tag is only needed on the page that has the action you want to track.'))}
            exampleTemplate={exampleCodeSnippet}
          />
        </React.Fragment>
      )}
    </div>
  );
}

CopyEventTagViewUnstyled.propTypes = {
  action: PropTypes.string,
  classes: PropTypes.shape({ copyEventTagView: PropTypes.string }).isRequired,
  currencyCode: PropTypes.string,
  goalValue: PropTypes.string,
  goalValueType: PropTypes.oneOf(['FixedValue', 'VariantValue', 'NoValue']).isRequired,
  onCopyToClipboardComplete: PropTypes.func,
  onViewEmailInstructionsClick: PropTypes.func.isRequired,
};

CopyEventTagViewUnstyled.defaultProps = {
  action: undefined,
  currencyCode: undefined,
  goalValue: undefined,
  onCopyToClipboardComplete: _.noop,
};

export const CopyEventTagView = withDefaultStyles(CopyEventTagViewUnstyled, getCopyEventTagViewStyles);
